import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Divider,
} from "@mui/material";
import "../styles.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from '@mui/icons-material/Edit';
import { networkUrls } from "../../../services/networkurls";
import { Get, Put } from "../../../services/apiServices";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Chip } from "@mui/material";
import { saveAs } from "file-saver";
import DeleteIcon from '@mui/icons-material/Delete';
import ReusableModal from "../../../components/ReusableModal/ReusableModal";
import ReuseableSnackbar from "../../../components/Snackbar/Snackbar";
import EditMetaAcronymModal from "../../../components/EditModal/EditMetaAcronymModal";
import constants from "../../../utils/constants";
import jwtDecoder from "../../../utils/jwtDecoder";
import EditPptModal from "../../../components/EditModal/EditPptModal";

const VideoDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const[filename,setFilename]=useState("");
  const BaseURL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "");
  const [video, setVideo] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [downloadLoader, setDownloadLoader] = useState(false);
 const[editModal,setEditModal]=useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [isShowSnackbar, setIsShowSnackbar] = useState(false);

  const [snackbarInfo, setSnackbarInfo] = useState({
    color: "",
    message: "",
  });

  const handleOpenDeleteModal = () => {
    setOpen(true);
  };

   const handleOpenEditModal = (name:string) => {
  setFilename(name);
  setEditModal(true);
 }

  const getSingleVideo = async () => {
    try {
      const response = await Get(`${networkUrls.getSingleVideo}/${id}`, true);
      if (response?.status === 200) {
        setVideo(response?.data || null);
      }
    } catch (error) {
      console.log(error, "err");
    } finally {
      setLoading(false);
    }
  };

  const updateMetadata = async (metadata: any, acronyms: any) => {
    try {
      const response = await Put(networkUrls.updateTranscriptMeatadata, { doc_id: id, metadata, acronyms }, true);
      if (response?.status === 200) {
        setIsShowSnackbar(true);
        setSnackbarInfo({ color: "success", message: "Metadata updated successfully" });
        getSingleVideo();
      } else {
        setIsShowSnackbar(true);
        setSnackbarInfo({ color: "error", message: "Failed to update metadata" });
      }
    } catch (error:any) {
      console.log(error, "err");
      setSnackbarInfo({ color: "error", message: error?.response?.data?.error || "An error occurred" });
    }
  };

  useEffect(() => {
    getSingleVideo();
  }, []);

  const downloadFile = async (fileId: string, filename: string) => {
    setDownloadLoader(true);
    const jwt = await jwtDecoder();
    try {
      const res = await fetch(`${BaseURL}${networkUrls.downloadCaseStudy}/${fileId}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Authorization": `Bearer ${jwt}`,
        },
      });

      if (!res.ok) throw new Error("Download failed");

      const blob = await res.blob();
      saveAs(blob, filename);
    } catch (err) {
      console.error("Download error:", err);
    } finally {
      setDownloadLoader(false);
    }
  };

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!video) {
    return (
      <Box p={4}>
        <Typography color="error">Video not found.</Typography>
      </Box>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <IconButton
        onClick={() => navigate(constants.navigationUrls.adminDashboard)}
        sx={{
          backgroundColor: "#4D8FAC",
          color: "#fff",
          "&:hover": {
            backgroundColor: "#3b7694",
          },
          mb: 2,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <Grid sx={{ display: "flex", justifyContent: "space-between" }}>
        <Grid sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography variant="h4" gutterBottom className="file-name">
            {video.filename || video.name}
          </Typography>
           <IconButton onClick={()=>handleOpenEditModal(video.filename)} title="Edit Document">
                  <EditIcon sx={{color:"#3b7694"}} className="edit-icon"/>
                </IconButton>
        </Grid>
        <IconButton onClick={handleOpenDeleteModal}>
          <DeleteIcon sx={{ color: "red" }} className="delete-icon" />
        </IconButton>
      </Grid>

      <Card sx={{ marginBottom: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" paddingBottom="4px">Video Summary</Typography>
          <Typography variant="body1" paragraph className="summary-text">
            {video.summary || "No summary available."}
          </Typography>

          <Divider />
          <Grid container spacing={2} sx={{ paddingTop: "4px" }}>
            <DetailBlock label="Industries" items={video.industries || []} acronyms={video.acronyms?.industries} />
            <DetailBlock label="Organizations" items={video.organizations || []} acronyms={video.acronyms?.organizations} />
            <DetailBlock label="Regions" items={video.regions || []} acronyms={video.acronyms?.regions} />
            <DetailBlock label="Technologies" items={video.technologies || []} acronyms={video.acronyms?.technologies} />
          </Grid>
         
              

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item>
              <Button
                variant="contained"
                startIcon={<VisibilityIcon />}
                sx={{ backgroundColor: "#4D8FAC" }}
                href={video.preview_link || video.drive_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Video
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                startIcon={downloadLoader ? <CircularProgress size={16} sx={{ color: "white" }} /> : <DownloadIcon />}
                onClick={() => downloadFile(video.drive_file_id, video.filename || video.name)}
                disabled={downloadLoader}
                sx={{ backgroundColor: "#4D8FAC" }}
              >
                {downloadLoader ? "Downloading..." : "Download Video"}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={() => setMetaModalOpen(true)}
                startIcon={<EditIcon />}
                sx={{ backgroundColor: "#4D8FAC" }}
              >
                Edit Tags
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {isShowSnackbar && (
        <ReuseableSnackbar
          isShowSnackbar={isShowSnackbar}
          colour={snackbarInfo.color}
          setIsShowSnackbar={setIsShowSnackbar}
          message={snackbarInfo.message}
          autoHideDuration={2000}
        />
      )}

             {editModal && <EditPptModal open={editModal} setOpen={setEditModal} type="video" name={filename} setIsShowSnackbar={setIsShowSnackbar} setSnackbarInfo={setSnackbarInfo} id={id}  getCall={getSingleVideo}/>}

      {open && (
        <ReusableModal
          open={open}
          setOpen={setOpen}
          type="delete"
          name={video.filename || video.name}
          setIsShowSnackbar={setIsShowSnackbar}
          setSnackbarInfo={setSnackbarInfo}
          id={id}
          category="video"
          getCall={getSingleVideo}
        />
      )}

      {metaModalOpen && (
        <EditMetaAcronymModal
          open={metaModalOpen}
          setOpen={setMetaModalOpen}
          initialMetadata={{
            organizations: video.organizations || [],
            industries: video.industries || [],
            technologies: video.technologies || [],
            regions: video.regions || [],
          }}
          initialAcronyms={video.acronyms || {
            organizations: [],
            industries: [],
            technologies: [],
            regions: [],
          }}
          loading={metaLoading}
          excludeDocumentTypes={true}
          onSubmit={async ({ metadata, acronyms }) => {
            setMetaLoading(true);
            await updateMetadata(metadata, acronyms);
            setMetaLoading(false);
            setMetaModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

interface DetailBlockProps {
  label: string;
  items: string[];
  acronyms?: string[];
}

const DetailBlock: React.FC<DetailBlockProps> = ({ label, items, acronyms }) => (
  <Grid item xs={12}>
    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
      {label}
    </Typography>
    {items && items.length > 0 ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        {items.map((item, i) => (
          <Chip
            key={i}
            label={
              acronyms && acronyms[i]
                ? `${item} (${acronyms[i]})`
                : item
            }
            variant="outlined"
          />
        ))}
      </Box>
    ) : (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        No {label.toLowerCase()} found
      </Typography>
    )}
    <Divider />
  </Grid>
);

export default VideoDetails;

